import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock } from "lucide-react";
import SEO from "@/components/SEO";
import { POSTS } from "@/data/blog";

const SITE_URL = "https://joehelfrickinjuryattorney.com";

export default function Blog() {
    const schema = {
        "@context": "https://schema.org",
        "@type": "Blog",
        "@id": `${SITE_URL}/blog#blog`,
        name: "Joseph G. Helfrick — Sacramento Personal Injury Blog",
        url: `${SITE_URL}/blog`,
        description:
            "Practical, plain-English guidance on personal injury law in Sacramento, CA, and surrounding areas, California — from a Sacramento-born trial attorney.",
        publisher: { "@id": `${SITE_URL}/#attorney` },
        blogPost: POSTS.map((p) => ({
            "@type": "BlogPosting",
            headline: p.title,
            url: `${SITE_URL}/blog/${p.slug}`,
            datePublished: p.dateISO,
            dateModified: `${p.updated}T08:00:00-08:00`,
            author: { "@id": `${SITE_URL}/#person` },
            image: `${SITE_URL}${p.image}`,
        })),
    };

    return (
        <>
            <SEO
                title="Blog — Sacramento Personal Injury Law"
                description="Plain-English guides to personal injury law in Sacramento, CA, and surrounding areas, written by a Sacramento trial attorney. Statutes of limitation, choosing an accident lawyer, damages, and more."
                path="/blog"
                schema={schema}
                breadcrumbs={[
                    { name: "Home", path: "/" },
                    { name: "Blog", path: "/blog" },
                ]}
            />

            <section
                data-testid="blog-hero"
                className="border-b border-[#1F3458]"
            >
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-20 pb-12 lg:pt-28 lg:pb-16">
                    <div className="text-[#D4AF37] text-xs uppercase tracking-[0.28em] font-bold">
                        Blog
                    </div>
                    <h1 className="mt-6 font-serif text-5xl lg:text-7xl text-[#F7F5EB] leading-[0.95] font-light max-w-4xl">
                        Plain-English guides{" "}
                        <span className="italic text-[#D4AF37]">
                            from a Sacramento trial attorney.
                        </span>
                    </h1>
                    <p className="mt-8 text-lg text-[#9FB3D4] max-w-2xl leading-relaxed">
                        No filler. No marketing fluff. Just the questions Joe
                        Helfrick actually gets from clients in Sacramento and
                        Placer County — answered straight.
                    </p>
                </div>
            </section>

            <section className="border-b border-[#1F3458]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 grid gap-6">
                    {POSTS.map((p, i) => (
                        <Link
                            key={p.slug}
                            data-testid={`blog-card-${p.slug}`}
                            to={`/blog/${p.slug}`}
                            className="group bg-[#11203D] border border-[#1F3458] hover:border-[#D4AF37] transition-colors p-8 md:p-12 grid md:grid-cols-12 gap-8 items-start"
                        >
                            <div className="md:col-span-2">
                                <div className="text-[#D4AF37] text-xs uppercase tracking-[0.28em] font-bold">
                                    0{i + 1}
                                </div>
                                <div className="mt-3 text-[#9FB3D4] text-xs uppercase tracking-[0.18em]">
                                    {p.category}
                                </div>
                            </div>
                            <div className="md:col-span-10">
                                <h2 className="font-serif text-3xl md:text-4xl text-[#F7F5EB] leading-tight">
                                    {p.title}
                                </h2>
                                <p className="mt-4 text-[#9FB3D4] leading-relaxed">
                                    {p.excerpt}
                                </p>
                                <div className="mt-6 flex flex-wrap items-center gap-5 text-xs text-[#9FB3D4]">
                                    <span className="flex items-center gap-2">
                                        <Calendar size={12} />
                                        {new Date(p.date).toLocaleDateString(
                                            "en-US",
                                            {
                                                year: "numeric",
                                                month: "long",
                                                day: "numeric",
                                            },
                                        )}
                                    </span>
                                    <span className="flex items-center gap-2">
                                        <Clock size={12} />
                                        {p.readTime}
                                    </span>
                                </div>
                                <div className="mt-6 inline-flex items-center gap-2 text-[#D4AF37] text-xs uppercase tracking-[0.22em] font-bold">
                                    Read article{" "}
                                    <ArrowRight
                                        size={14}
                                        className="group-hover:translate-x-1 transition-transform"
                                    />
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section data-testid="blog-cta">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 grid lg:grid-cols-12 gap-12 items-end">
                    <div className="lg:col-span-7">
                        <h2 className="font-serif text-4xl lg:text-5xl text-[#F7F5EB] leading-tight">
                            Reading about your situation?{" "}
                            <span className="italic text-[#D4AF37]">
                                Get an answer about it.
                            </span>
                        </h2>
                        <p className="mt-5 text-[#9FB3D4] text-lg max-w-xl">
                            Sixty seconds, seven questions, and your case lands
                            in front of Joe with the details organized. Free and
                            confidential.
                        </p>
                    </div>
                    <div className="lg:col-span-5">
                        <Link
                            to="/case-evaluator"
                            data-testid="blog-cta-evaluator"
                            className="inline-flex items-center gap-3 bg-[#D4AF37] text-[#07142B] font-bold uppercase tracking-wider text-sm px-8 py-4 hover:bg-[#B8962A] transition-colors"
                        >
                            Take the 60-Second Evaluator{" "}
                            <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>
        </>
    );
}
